// src/hooks/useInventoryWarehouseKPIPermissions.ts
import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { INVENTORY_PERMISSIONS } from '../constants/permissions.constants';

/**
 * Hook to get all warehouse KPI-related permissions in one object
 *
 * Used for controlling visibility of KPI cards in inventory stats
 *
 * @returns Object with all warehouse KPI permission flags
 *
 * @example
 * ```tsx
 * const kpiPerms = useInventoryWarehouseKPIPermissions();
 * if (kpiPerms.canKpiTotalProducts) {
 *   // Show total products KPI card
 * }
 * ```
 */
export const useInventoryWarehouseKPIPermissions = () => {
  const canKpiTotalProducts = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_TOTAL_PRODUCTS);
  const canKpiTotalSaleValue = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_TOTAL_SALE_VALUE);
  const canKpiTotalCostValue = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_TOTAL_COST_VALUE);
  const canKpiUnassigned = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_UNASSIGNED);
  const canKpiAssigned = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_ASSIGNED);
  const canKpiInTransit = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_IN_TRANSIT);
  const canKpiBajas = usePermission(INVENTORY_PERMISSIONS.WAREHOUSE.KPI_BAJAS);
  const canKpiWarehouseReserved = usePermission(
    INVENTORY_PERMISSIONS.WAREHOUSE.KPI_WAREHOUSE_RESERVED,
  );

  return useMemo(
    () => ({
      canKpiTotalProducts,
      canKpiTotalSaleValue,
      canKpiTotalCostValue,
      canKpiUnassigned,
      canKpiAssigned,
      canKpiInTransit,
      canKpiBajas,
      canKpiWarehouseReserved,
    }),
    [
      canKpiTotalProducts,
      canKpiTotalSaleValue,
      canKpiTotalCostValue,
      canKpiUnassigned,
      canKpiAssigned,
      canKpiInTransit,
      canKpiBajas,
      canKpiWarehouseReserved,
    ],
  );
};
